import { ref, readonly } from 'vue';
import { deleteRequest, ENDPOINTS, guideErrorMessage, guideHttpStatus } from '@/api/mainApi';
import { isDigitalPayment } from '@/classes/PaymentMethod';
import store from '@/store';
import { PaymentMethod } from '@/interfaces/economy/paymentMethod';
import { Donation } from '@/interfaces/donation/donation';

/** Next gift must not keep the previous crop key or preview data URL. */
export function donationWithoutCertificatePhoto(donation: Donation): Donation {
  return {
    ...donation,
    imageKey: null,
    customCertificateImageUrl: null,
  };
}

export function useDonationReset() {
  const loading = ref(false);
  const error = ref<string | null>(null);

  const resetDonation = async (donation: Donation): Promise<void> => {
    loading.value = true;
    error.value = null;

    try {
      const paymentMethod = donation.paymentSpecification?.paymentMethod as PaymentMethod;
      const isDigital = isDigitalPayment(paymentMethod.type);
      const paid = !!donation.paymentSpecification?.paid;
      const nextDonation = donationWithoutCertificatePhoto(donation);

      if (isDigital) {
        if (paid) {
          nextDonation.id = null;
        } else {
          await deleteRequest(`${ENDPOINTS.PAYMENTS}/${ENDPOINTS.TRANSACTIONS}`, {
            donationId: donation.id,
          });
        }
      } else {
        // Finalize/cancel invoice and reset donation ID
        await deleteRequest(`${ENDPOINTS.INVOICE}/${ENDPOINTS.FINALIZE}`, {
          donationId: donation.id,
        });
        nextDonation.id = null;
      }
      store.commit('updateDonation', nextDonation);
      window.history.replaceState({}, '', window.location.pathname);
    } catch (err) {
      error.value = 'Failed to reset donation';
      window.log?.error('[Guide] donation.reset — Failed to reset donation', {
        message: guideErrorMessage(err),
        httpStatus: guideHttpStatus(err),
        donationId: donation.id,
        serialnumber: donation.serialnumber,
      });
      store.commit('setSnackbar', { active: true, color: 'error', text: error.value });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  return {
    loading: readonly(loading),
    error: readonly(error),
    resetDonation,
  };
}
